import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AgendaEmployeeReportRow } from '../../../appointments/infra/appointmentsRepo';
import { reportAppointmentsByEmployee } from '../../../appointments/infra/appointmentsRepo';
import { getReportsDateRange } from '../reportsScreen.utils';
import type { ReportsCustomRange } from '../reportsScreen.utils';
import type { DateRangePreset } from '../reportsScreen.constants';

type Params = {
  currentBranchId: string | undefined;
  dateRangePreset: DateRangePreset;
  customRange: ReportsCustomRange | null;
  shouldLoad: boolean;
};

export function useReportsAgenda({
  currentBranchId,
  dateRangePreset,
  customRange,
  shouldLoad,
}: Params) {
  const [rows, setRows] = useState<AgendaEmployeeReportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!currentBranchId || !shouldLoad) return;
    setLoading(true);
    setError(null);
    try {
      const range = getReportsDateRange(new Date(), dateRangePreset, customRange);
      if (!range) {
        setRows([]);
        return;
      }
      const data = await reportAppointmentsByEmployee(currentBranchId, range.fromIso, range.toIso);
      setRows(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudieron cargar los turnos.');
    } finally {
      setLoading(false);
    }
  }, [currentBranchId, dateRangePreset, customRange, shouldLoad]);

  useEffect(() => {
    void load();
  }, [load]);

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, row) => ({
        requested: acc.requested + row.requestedCount,
        confirmed: acc.confirmed + row.confirmedCount,
        completed: acc.completed + row.completedCount,
        noShow: acc.noShow + row.noShowCount,
        cancelled: acc.cancelled + row.cancelledCount,
        charged: acc.charged + row.chargedCount,
        amount: acc.amount + row.totalAmount,
        minutes: acc.minutes + row.totalMinutes,
      }),
      { requested: 0, confirmed: 0, completed: 0, noShow: 0, cancelled: 0, charged: 0, amount: 0, minutes: 0 }
    );
  }, [rows]);

  return { rows, totals, loading, error, reload: load };
}
